import styled, { css, keyframes } from 'styled-components'

import { IconClose2 } from '@avantstay/backoffice-vectors'

import {
  _2XL_Bold,
  _XL_Bold,
  HEADER_HEIGHT,
  HEADER_WITH_SECONDARY_MENU_HEIGHT,
  mainColors,
  medias,
  shadowColors,
  zIndex,
} from '../../styles'
import { rgba } from '../../utils'
import { DEFAULT_ATTACHMENT_BAR_WIDTH } from '../../utils/attachmentBarPresence'
import { AttachmentBarContainerProps } from './AttachmentBar.types'

export const CONTAINER_WIDTH = `${DEFAULT_ATTACHMENT_BAR_WIDTH}px`

const slideIn = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(0%); }
`

const slideOut = keyframes`
  0% { transform: translateX(0%); }
  100% { transform: translateX(100%); }
`

const containerWithFilterStyle = css`
  background: ${mainColors.neutralExtraLight};
  border-bottom: 1px solid ${mainColors.neutralMedium};
`

export const OutsideContainer = styled.div`
  position: fixed;
  z-index: ${zIndex.SIDEBAR_SHADOW};
  inset: 0;
  width: 100%;
  height: 100%;
  background-color: ${rgba(mainColors.black, 0.05)};
`

export const Container = styled.div<AttachmentBarContainerProps>`
  position: fixed;
  top: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  z-index: ${zIndex.FIXED_ACTIONS_MOBILE_CONTAINER};
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  box-shadow: -9px 4px 50px ${rgba(shadowColors.blur, 0.3)};
  background: ${mainColors.white};
  border-left: 1px solid ${mainColors.neutralLight};

  /* Animate horizontal reflow when neighboring tiled panels open/close. */
  transition: right 0.25s ease;

  ${medias.XS_SCREEN`
    animation-duration: 0.25s;
  `}

  ${medias.LG_SCREEN`
    z-index: ${zIndex.PORTAL};
  `}

  ${({ screenHasSecondaryMenu, hasTransitionAnimation, rightOffset, width }) => medias.LG_SCREEN`
    width: ${width ? `${width}px` : CONTAINER_WIDTH};

    right: ${rightOffset ?? 0}px;

    height: calc(100% - ${
      screenHasSecondaryMenu ? HEADER_WITH_SECONDARY_MENU_HEIGHT : HEADER_HEIGHT
    });

    top: ${screenHasSecondaryMenu ? HEADER_WITH_SECONDARY_MENU_HEIGHT : HEADER_HEIGHT};

    animation-duration: ${hasTransitionAnimation ? '0.25s' : '0s'};
  `}

  pointer-events: ${p => (p.unmounting ? 'none' : 'all')};

  animation-duration: ${p => (p.hasTransitionAnimation ? '0.25s' : '0s')};
  animation-fill-mode: ${p => (p.unmounting ? 'forwards' : 'backwards')};
  animation-name: ${p => (p.unmounting ? slideOut : slideIn)};
`

// Left-edge grip that drag-resizes the panel horizontally (rendered only with
// the `resizable` prop). Hidden below LG_SCREEN, where the panel is full width
// and the `width` prop has no effect either. High z-index: the grip must stay
// grabbable even when panel content stacks its own overlays (drop zones,
// in-panel dialogs) over the chat area.
export const ResizeHandle = styled.div<{ $active?: boolean }>`
  display: none;

  ${({ $active }) => medias.LG_SCREEN`
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 6px;
    z-index: 30;
    cursor: col-resize;
    touch-action: none;
    background: ${$active ? mainColors.accentMedium : 'transparent'};
    transition: background-color 0.15s ease;

    &:hover {
      background: ${$active ? mainColors.accentMedium : mainColors.accentLight};
    }
  `}
`

export const Header = styled.div<{ hasFilter?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: row-reverse;
  padding: 30px 15px 20px;
  flex-wrap: wrap;
  position: relative;

  ${p => p.hasFilter && containerWithFilterStyle};

  ${medias.MD_SCREEN`
    justify-content: space-between;
    flex-direction: row;
    padding: 20px 30px;
  `}
`

export const Title = styled.h2`
  ${_2XL_Bold};
  line-height: 26px;
  color: ${mainColors.black};
  width: 100%;
  flex-grow: 1;

  ${medias.MD_SCREEN`
    ${_XL_Bold};
    line-height: 24px;
    width: auto;
    /* A zero basis keeps a long title from wrapping the toggle and close button onto the next
       line; the title shrinks and its content wraps or truncates instead. */
    flex: 1 1 0;
    min-width: 0;
  `}
`

// Toggles the user preference between pushing the page content aside and
// overlaying it. Hidden on small screens, where panels are full width and the
// preference has no effect.
export const ContentModeToggleButton = styled.button`
  display: none;

  ${medias.MD_SCREEN`
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0 12px;
    color: ${mainColors.neutralExtraDark};
    background: transparent;
    border: 0;
    cursor: pointer;

    &:hover {
      color: ${mainColors.neutralExtremelyDark};
    }

    svg {
      width: 20px;
      height: 20px;
    }
  `}
`

export const CloseButton = styled(IconClose2)`
  position: absolute;
  right: 20px;
  top: 28px;
  height: 30px;
  width: 30px;
  color: ${mainColors.neutralExtraDark};
  cursor: pointer;

  &:hover {
    color: ${mainColors.neutralExtremelyDark};
  }

  ${medias.MD_SCREEN`
    position: relative;
    flex: none;
    top: 0;
    left: 0;
    height: 24px;
    width: 24px;
    margin: 0;
  `}
`

export const FilterContainer = styled.div`
  width: 100%;
  margin: -10px 0 -15px;

  & > div > div {
    padding: 13px 20px;
  }

  ${medias.MD_SCREEN`
    width: auto;
    transform: translate(25px, -10px);

      & > div > div {
        padding: 5px;
      }
  `}
`

export const Footer = styled.div<{ width?: number }>`
  border-top: 1px solid ${mainColors.neutralMedium};
  padding: 20px 0;
  width: 100%;

  ${({ width }) => medias.MD_SCREEN`
    width: ${width ? `${width}px` : CONTAINER_WIDTH};
  `}
`
